import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Typography,
  Tooltip,
  DebouncedInput,
  Button,
} from "@altimateai/lego";
import { IconSquare, IconSquareCheckFilled, IconSearch } from "@tabler/icons-react";
import { Stack, Spinner, LoadingButton } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";

export interface FilterOption {
  label: string | React.ReactNode;
  value: string;
  showTooltip?: boolean;
}

// Default extractor function for backward compatibility
const defaultExtractor = <T,>(data: T): FilterOption[] => {
  // Handle response that might be an array or object with items
  const responseData = data as { items?: FilterOption[] } | FilterOption[];
  const items = Array.isArray(responseData) ? responseData : (responseData.items || []);
  return items.map((item) => ({
    label: item.label,
    value: item.value,
  }));
};

interface APIFilterDropdownProps<TData = unknown> {
  id?: string;
  label: string;
  apiFunction: (params: { prefix: string; page: number; size: number }) => Promise<TData>;
  onChange: (value: string[]) => void;
  value: string[];
  disabled?: boolean;
  seeMore?: boolean;
  searchKey?: string;
  extractorFunction?: (data: TData) => FilterOption[];
  pageSize?: number;
  children?: React.ReactNode; // Ignored - for backward compatibility only
}

export const APIFilterDropdown = <TData = unknown,>({
  id = "api-filter-dropdown",
  label,
  apiFunction,
  onChange,
  value,
  disabled = false,
  seeMore = false,
  searchKey,
  extractorFunction,
  pageSize = 10,
}: APIFilterDropdownProps<TData>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSearchValue, setCurrentSearchValue] = useState("");
  const [accumulatedOptions, setAccumulatedOptions] = useState<FilterOption[]>([]);
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set(value));
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const commandListRef = useRef<HTMLDivElement>(null);

  // Sync external value changes with internal state
  useEffect(() => {
    setSelectedValues(new Set(value));
  }, [value]);

  const { data, isFetching, isError } = useQuery({
    queryKey: [id, currentSearchValue, currentPage, pageSize],
    queryFn: () => apiFunction({ prefix: currentSearchValue, page: currentPage, size: pageSize }),
    placeholderData: keepPreviousData,
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      const extractor = extractorFunction || defaultExtractor;
      const newOptions = extractor(data);

      // For pagination, append to existing options; for new search, replace
      if (seeMore && currentPage > 1) {
        setAccumulatedOptions(prev => [...prev, ...newOptions]);
        // Scroll to bottom to show newly loaded items
        setTimeout(() => {
          if (commandListRef.current) {
            commandListRef.current.scrollTop = commandListRef.current.scrollHeight;
          }
        }, 50);
      } else {
        setAccumulatedOptions(newOptions);
      }
    }
  }, [data, seeMore, currentPage, extractorFunction]);


  // Calculate total pages and total count from response
  const responseWithMeta = data as { pages?: number, total_pages?: number; total?: number } | undefined;
  const totalPages = seeMore && responseWithMeta ? (responseWithMeta.total_pages || responseWithMeta.pages || 1) : 1;
  const totalCount = responseWithMeta?.total || 0;
  const hasMore = currentPage < totalPages;

  const handleSearch = useCallback((searchValue: string) => {
    setCurrentSearchValue(searchValue);
    setCurrentPage(1);
    setAccumulatedOptions([]);
  }, []);

  const handleToggleValue = (optionValue: string) => {
    setSelectedValues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(optionValue)) {
        newSet.delete(optionValue);
      } else {
        newSet.add(optionValue);
      }
      return newSet;
    });
  };

  const handleSeeMore = () => {
    if (hasMore && !isFetching) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleApply = () => {
    const values = Array.from(selectedValues);

    if (searchKey) {
      updateSearchParamsByKey({ [searchKey]: values });
    }
    onChange(values);
  };

  return (
    <div aria-label={`${label} filter dropdown`}>
      <Command shouldFilter={false}>
        <Stack justify="between" align="center" className="al-p-2">
          <Typography variant="body" size="sm">{label}</Typography>
          <Button onClick={handleApply} size="sm" disabled={disabled}>
            Apply
          </Button>
        </Stack>
        <div className="al-px-2">
          <DebouncedInput
            debounceMs={500}
            placeholder={`Search ${label}...`}
            onChange={handleSearch}
            icon={IconSearch}
          />
        </div>
        <CommandList ref={commandListRef} className="al-max-h-80 al-overflow-y-auto">
          {isFetching && accumulatedOptions.length === 0 ? (
            <Stack justify="center" align="center" className="al-p-4">
              <Spinner size="md" />
            </Stack>
          ) : isError ? (
            <Stack justify="center" align="center" className="al-p-4">
              <Typography variant="body" size="sm" className="al-text-destructive">
                Failed to load options
              </Typography>
            </Stack>
          ) : (
            <>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {accumulatedOptions.map(option => {
                  const shouldShowTooltip = option.showTooltip ?? typeof option.label === "string";
                  const tooltipTitle = typeof option.label === "string" ? option.label : option.value;

                  const content = (
                    <div className="al-w-full al-min-w-0">
                      <Stack align="center" gap="sm" className="al-w-full al-min-w-0">
                        {selectedValues.has(option.value) ? (
                          <IconSquareCheckFilled className="al-size-4 al-text-primary al-shrink-0" />
                        ) : (
                          <IconSquare className="al-size-4 al-text-muted-foreground al-shrink-0" />
                        )}
                        <span className="al-text-sm al-truncate al-flex-1 al-min-w-0">
                          {option.label}
                        </span>
                      </Stack>
                    </div>
                  );

                  return (
                    <CommandItem
                      key={option.value}
                      value={typeof option.label === "string" ? option.label : option.value}
                      onSelect={() => handleToggleValue(option.value)}
                      className="al-cursor-pointer"
                    >
                      {shouldShowTooltip ? (
                        <Tooltip title={tooltipTitle}>{content}</Tooltip>
                      ) : (
                        content
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}
        </CommandList>
        {seeMore && accumulatedOptions.length > 0 && hasMore && (
          <div className="al-p-1">
            <LoadingButton
              size="sm"
              variant="outline"
              className="al-w-full"
              onClick={handleSeeMore}
              loading={isFetching}
            >
              {`See next ${pageSize} of ${totalCount}`}
            </LoadingButton>
          </div>
        )}
      </Command>
    </div>
  );
};
